// File-type icon/color table + card renderer, adapted from
// directMessageChat/mixins/renderingMixin.js's getFileTypeInfo/
// renderGenericFileCardHtml. Duplicated rather than shared/extracted - lower
// blast radius for a first pass than refactoring the heavily-used
// directMessageChat component (see plan section G).
const FILE_TYPES = [
    { label: 'PDF', color: '#e5252a', mimes: ['application/pdf'], exts: ['pdf'] },
    { label: 'DOC', color: '#2b579a', mimes: ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'], exts: ['doc', 'docx'] },
    { label: 'XLS', color: '#217346', mimes: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'], exts: ['xls', 'xlsx'] },
    { label: 'PPT', color: '#d24726', mimes: ['application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'], exts: ['ppt', 'pptx'] },
    { label: 'ZIP', color: '#e8a020', mimes: ['application/zip', 'application/x-zip-compressed'], exts: ['zip', 'rar', '7z', 'tar', 'gz'] },
    { label: 'TXT', color: '#767676', mimes: ['text/plain'], exts: ['txt', 'log'] },
];

function getFileTypeInfo(mimeType, fileName) {
    const ext = (fileName || '').split('.').pop().toLowerCase();
    return FILE_TYPES.find(t => t.mimes.includes(mimeType) || t.exts.includes(ext)) || null;
}

export const emailRenderingMixin = {

    //********************* ESCAPE HTML ********************************//
    // Every string that originates from a synced email (subject, from name,
    // addresses, attachment names, snippet) is untrusted and must go through
    // this before being interpolated into the component's rendered HTML -
    // the body itself is additionally isolated in a sandboxed iframe (see
    // buildMessageBodySrcdoc) since it can be full untrusted HTML, not just text.
    // * value (any)
    escapeHtml(value) {
        return String(value ?? '').replace(/[&<>"']/g, c => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[c]));
    },

    //********************* GET TIME AGO ********************************//
    // * timestamp (number) — epoch ms
    getTimeAgo(timestamp) {
        const diff = Date.now() - timestamp;
        const mins = Math.floor(diff / 60000);

        if(mins < 1) return 'just now';
        if(mins < 60) return `${mins}m ago`;

        const hours = Math.floor(mins / 60);
        if(hours < 24) return `${hours}h ago`;

        const days = Math.floor(hours / 24);
        if(days < 7) return `${days}d ago`;

        return new Date(timestamp).toLocaleDateString();
    },

    //********************* FORMAT FILE SIZE ********************************//
    // * bytes (number)
    formatFileSize(bytes) {
        if(bytes == null) return '';
        if(bytes < 1024) return `${bytes} B`;
        if(bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },

    //********************* FORMAT FROM ********************************//
    // * from ({name, address}) — a message's `from` field
    formatFrom(from) {
        if(!from) return '';
        const name = this.escapeHtml(from.name || from.address || '');
        return name;
    },

    //********************* FORMAT ADDRESS LIST ********************************//
    // * addresses (Array<{name, address}>)
    formatAddressList(addresses) {
        return (addresses || [])
            .map(a => this.escapeHtml(a.name ? `${a.name} <${a.address}>` : a.address))
            .join(', ');
    },

    //********************* RENDER ATTACHMENT CARD ********************************//
    // Compact card: icon-by-type badge, filename, size, download link (opens
    // the file's already-absolute, hydrated `url` - see routes.js's
    // hydrateAttachment). No delete action - inbound email attachments aren't
    // user-deletable the way gudhub-messenger uploads are.
    // * attachment ({filename, name, size, mime_type, url})
    renderAttachmentCard(attachment) {
        const typeInfo = getFileTypeInfo(attachment.mime_type, attachment.name);
        const isImage = attachment.mime_type?.startsWith('image/');

        const preview = attachment.thumbnail_url
            ? `<img class="email_attachment__thumb" src="${this.escapeHtml(attachment.thumbnail_url)}" alt="">`
            : typeInfo
                ? `<span class="email_attachment__badge" style="background:${typeInfo.color}">${typeInfo.label}</span>`
                : `<span class="email_attachment__badge email_attachment__badge--generic">FILE</span>`;

        return `<a class="email_attachment ${isImage ? 'email_attachment--image' : ''}" href="${this.escapeHtml(attachment.url)}" target="_blank" rel="noopener noreferrer" download="${this.escapeHtml(attachment.name)}" title="Download ${this.escapeHtml(attachment.name)}">
            <div class="email_attachment__preview">${preview}</div>
            <div class="email_attachment__info">
                <span class="email_attachment__name">${this.escapeHtml(attachment.name)}</span>
                ${attachment.size != null ? `<span class="email_attachment__size">${this.formatFileSize(attachment.size)}</span>` : ''}
            </div>
        </a>`;
    },

    //********************* BUILD MESSAGE BODY SRCDOC ********************************//
    // A message's body_html is raw, untrusted HTML straight from the sender -
    // it must never be injected directly into the page's own DOM (script tags
    // aside, inline event handlers like onerror/onload execute even via
    // innerHTML). Instead it's rendered inside a sandboxed <iframe srcdoc="">
    // with only `allow-same-origin` granted (no `allow-scripts`), which lets
    // the parent measure/resize the frame (see wireMessageFrames) while any
    // scripts in the email body are inert. Falls back to escaped plain text
    // wrapped in <pre> when there's no HTML body.
    // * message ({body_html, body_text})
    buildMessageBodySrcdoc(message) {
        const inner = message.body_html
            ? message.body_html
            : `<pre style="white-space:pre-wrap;font-family:inherit;margin:0;">${this.escapeHtml(message.body_text || '')}</pre>`;

        return `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>
            body{margin:0;padding:12px;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;font-size:14px;color:#1a1a1a;word-wrap:break-word;overflow-wrap:break-word;}
            img{max-width:100%;height:auto;}
            a{color:#0d99ff;}
        </style></head><body>${inner}</body></html>`;
    },

};
